import React, {useEffect, useState} from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {invalidate, prefetch} from "services/query-client";
import {LibraryIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {useNavigate, useSearchParams} from "react-router-dom";
import {Exhibit, ExhibitData} from "types/Exhibit";
import DeleteExhibit from '../Delete';
import {useOperator} from "context/OperatorContext";
import BooleanIndicator from "components/BooleanIndicator";
import FeedbackBadge from "components/FeedbackBadge";
import {useAuth} from "context/AuthContext";
import NotAuthorised from "pages/NotAuthorised";
import ExhibitContextMenu from "components/menus/ExhibitContextMenu";
import {useNotifications} from "../../../context/NotificationContext";
import {secondsToHuman} from "../../../services/date";
import FloatingDropdown from "../../../components/FloatingDropdown";
import {Site} from "../../../types/Site";
import {getStoredItem, setStoredItem, STORAGE_KEYS} from "../../../services/storage";
import {isValidSiteId} from "../../../services/site";

// Types ----

type ExhibitUpdate = {
  id: number
  data: ExhibitData
}

type SiteItem = {
  label: string
  value: string | null
}

// Component ----

function ExhibitList() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const navigate = useNavigate();
  const {notify} = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams({
    site: '',
  });

  const [deletingExhibit, setDeletingExhibit] = useState<Exhibit | null>(null);
  const [page, setPage] = useState<number>(1);

  const siteIdParam = searchParams.get('site') ?? '';

  const {data, isLoading: exhibitsLoading, isFetching: exhibitsFetching, refetch: refetchExhibits} = useQuery(
    ['exhibits.all', {page, per_page: 50, sites: siteIdParam}],
    () => ngage.exhibits.filtered({page, per_page: 50, sites: siteIdParam || undefined}),
    {keepPreviousData: true, enabled: auth.can('exhibits:read')}
  )

  const {data: sites, isLoading: sitesLoading} = useQuery(['sites.all'], () => ngage.sites.all());

  const {mutateAsync: updateExhibit} = useMutation(
    (update: ExhibitUpdate) => ngage.exhibits.update(update.id, update.data)
  );

  // Check URL params or local storage for selected site id when sites are loaded
  useEffect(() => {
    if (!sites?.data) return;

    const storedSiteId = getStoredItem(STORAGE_KEYS.EXHIBITS_SITE_ID);
    const defaultSiteId = storedSiteId ?? '';
    const siteId = isValidSiteId(sites.data, siteIdParam) ? siteIdParam : defaultSiteId;

    setSearchParams(currentParams => {
      currentParams.set('site', siteId);
      return currentParams;
    }, { replace: true });

  }, [siteIdParam, sites?.data, setSearchParams])

  async function toggleExhibitPublishStatus(exhibit: Exhibit) {
    const publishing = !exhibit.is_published

    try {
      await updateExhibit({
        id: exhibit.id,
        data: {is_published: publishing}
      })

      void invalidate('exhibits')

      if (publishing) {
        notify("success", `${termFor('Exhibit')} Published`, `${exhibit.name} has been published`)
      } else {
        notify("success", `${termFor('Exhibit')} Unpublished`, `${exhibit.name} has been unpublished`)
      }
    } catch (error: any) {
      notify("error", "Something Went Wrong", `${exhibit.name} published status could not be changed`)
    }
  }

  // Create site items for dropdown
  function getSiteItems(sites: Site[] = []): SiteItem[] {
    const allSites: SiteItem = {label: `All ${termFor('sites')}`, value: null};
    const dynamicTypes = sites.map(site => ({label: site.name, value: site.id.toString()}));

    return [allSites, ...dynamicTypes];
  }

  // Get site items for dropdown
  const siteItems = sites?.data ? getSiteItems(sites.data) : [];

  // Set selected site
  function selectSite(siteId: number | null) {
    const siteIdString = siteId?.toString() ?? '';
    setSearchParams(currentParams => {
      currentParams.set('site', siteIdString);
      return currentParams;
    }, { replace: true });
    setStoredItem(STORAGE_KEYS.EXHIBITS_SITE_ID, siteIdString);
  }

  // Prefetch next and previous pages
  prefetch('exhibits.all', {page}, ngage.exhibits.all)

  // Prevent unauthorised viewing
  if (auth.cannot('exhibits:read')) {
    return <NotAuthorised/>
  }

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('exhibits:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/exhibits/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {'App Content': '/', 'Site': '/', [termFor('Exhibits')]: '/exhibits'};

  return (
    <Page
      title={termFor('Exhibits')}
      actions={actions}
      breadcrumbs={breadcrumbs}
      isLoading={exhibitsLoading || sitesLoading}
    >
      <div className="flex flex-col gap-5">
        <header className='flex justify-between gap-2 items-end'>
          {/* Refresh button */}
          <Button
            onClick={() => refetchExhibits()}
            showSpinner={exhibitsFetching}
            disabled={exhibitsFetching}
            size={Button.sizes.TINY}
            color={Button.colors.SECONDARY}
          >
            {exhibitsFetching ? 'Refreshing' : 'Refresh'}
          </Button>

          <div className='flex gap-2'>
            {/* Site filter */}
            <FloatingDropdown
              label={termFor('Site')}
              items={siteItems}
              selectedIndex={Math.max(siteItems.findIndex((item) => item.value === siteIdParam), 0)}
              onSelect={(item) => selectSite(item.value)}
            />
          </div>
        </header>

        <Table paginationData={data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>{termFor('Exhibit Type')}</TableHeader>
            <TableHeader>{termFor('Zone')}</TableHeader>
            <TableHeader>{termFor('Site')}</TableHeader>
            <TableHeader>Published</TableHeader>
            <TableHeader>Visible</TableHeader>
            <TableHeader>Avg. Dwell Duration</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Feedback</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={exhibitsLoading}>

          {data && data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={9}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <LibraryIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('exhibits')}</span>
                </div>
              </TableData>
            </TableRow>
            : data?.data.map((exhibit, index) => (
              <TableRow key={exhibit.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <ExhibitContextMenu
                        exhibit={exhibit}
                        onPublishExhibit={() => toggleExhibitPublishStatus(exhibit)}
                        onDeleteExhibit={() => setDeletingExhibit(exhibit)}
                        position="left"
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <LibraryIcon className="h-5 w-5 mr-2 text-gray-500"/> {exhibit.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{exhibit.type.name}</TableData>
                <TableData>{exhibit.zone.name}</TableData>
                <TableData>{exhibit.zone.site.name}</TableData>
                <TableData><BooleanIndicator value={exhibit.is_published}/></TableData>
                <TableData><BooleanIndicator value={!exhibit.is_hidden}/></TableData>
                <TableData>{exhibit.average_dwell_duration ? secondsToHuman(exhibit.average_dwell_duration) : 'N/A'}</TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <FeedbackBadge
                    count={exhibit.aggregated_feedback?.count ?? 0}
                    positiveRate={exhibit.aggregated_feedback?.positive_rate ?? 0}
                  />
                </TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <ExhibitContextMenu
                    exhibit={exhibit}
                    onPublishExhibit={() => toggleExhibitPublishStatus(exhibit)}
                    onDeleteExhibit={() => setDeletingExhibit(exhibit)}
                  />
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteExhibit
                  exhibit={deletingExhibit}
                  show={exhibit === deletingExhibit}
                  onCancel={() => setDeletingExhibit(null)}
                  onDelete={() => navigate('/exhibits')}
                  onDeleteFail={() => setDeletingExhibit(null)}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
      </div>
    </Page>
  );
}

export default ExhibitList;